import { useState, useEffect, useMemo, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import {
  Box, Button, TextField, Typography, CircularProgress, Switch,
  Autocomplete, FormControl, Select, MenuItem, Checkbox,
} from '@mui/material';
import {
  ViewColumn as ViewColumnIcon,
  Save as SaveIcon,
  CheckBox as CheckBoxIcon,
  CheckBoxOutlineBlank as CheckBoxBlankIcon,
} from '@mui/icons-material';
import { useAuth } from '../context/AuthContext';
import { useRights } from '../context/RightsContext';
import { getCurrentFYear } from '../utils/financialYear';
import { APPBAR_HEIGHT } from '../utils/theme';

const C = {
  blue: '#1877F2',
  blueDark: '#1464CC',
  blueLight: '#E7F0FD',
  blueMid: '#D0E4FC',
  grey200: '#E5E7EB',
  grey300: '#D1D5DB',
  grey400: '#9CA3AF',
  grey600: '#4B5563',
  grey800: '#1F2937',
  arctic: '#FAFAFA',
  white: '#FFFFFF',
  surface: '#F4F6FB',
  danger: '#DC2626',
};

const ios = {
  width: 34, height: 19, p: 0,
  '& .MuiSwitch-switchBase': {
    p: 0, m: '2px',
    transition: 'transform 0.18s cubic-bezier(0.4,0,0.2,1)',
    '&.Mui-checked': {
      transform: 'translateX(15px)', color: C.white,
      '& + .MuiSwitch-track': { bgcolor: C.blue, opacity: 1 },
    },
  },
  '& .MuiSwitch-thumb': { width: 15, height: 15, bgcolor: C.white, boxShadow: '0 1px 3px rgba(0,0,0,0.18)' },
  '& .MuiSwitch-track': { borderRadius: 10, bgcolor: C.grey300, opacity: 1 },
};

const CHARGE_COUNT = 5;
const TRANSTYPE_BASE = 200;

const blankCharge = (srNo) => ({
  SrNo: srNo,
  TransType: TRANSTYPE_BASE + srNo - 1,
  ChargeName: '',
  TaxEnabled: false,
  TaxId: null,
  HsnId: null,
  PartyId: null,
  ColToBeAsk: false,
  tax: null,
  hsn: null,
  client: null,
});

export default function UserDefineCharges({ connectionString }) {
  const { selectedCompany, user } = useAuth();
  const companyId = selectedCompany?.CompId;
  const userId = user?.UserId;
  const navigate = useNavigate();
  const { canAccess, showAccessDenied, rightsReady, rightsLoading } = useRights();

  useEffect(() => {
    if (!rightsReady || rightsLoading) return;
    if (canAccess && !canAccess('UserDefine', 'view')) {
      showAccessDenied?.();
      navigate('/main/home', { replace: true });
    }
  }, [rightsReady, rightsLoading, canAccess, showAccessDenied, navigate]);

  const [charges, setCharges] = useState(() =>
    Array.from({ length: CHARGE_COUNT }, (_, i) => blankCharge(i + 1))
  );
  const [chargeErrors, setChargeErrors] = useState({});
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [gstOptions, setGstOptions] = useState([]);
  const [hsnOptions, setHsnOptions] = useState([]);

  useEffect(() => {
    if (!companyId || !connectionString) return;
    setLoading(true);
    axios.get(`${connectionString}/api/userdefinecharges?companyId=${companyId}&fYear=${getCurrentFYear()}`)
      .then(res => {
        if (res.data.success && res.data.charges) {
          const dbCharges = res.data.charges;
          const slots = Array.from({ length: CHARGE_COUNT }, (_, i) => blankCharge(i + 1));
          dbCharges.forEach(dbCharge => {
            const idx = Number(dbCharge.SrNo) - 1;
            if (idx >= 0 && idx < CHARGE_COUNT) {
              slots[idx] = {
                ...dbCharge,
                TaxEnabled: dbCharge.TaxEnabled === 1 || dbCharge.TaxEnabled === true,
                ColToBeAsk: dbCharge.ColToBeAsk === 1 || dbCharge.ColToBeAsk === true,
                tax: dbCharge.TaxId != null ? { TaxID: dbCharge.TaxId, Taxcode: dbCharge.Taxcode } : null,
                hsn: dbCharge.HsnId != null ? { HSNId: dbCharge.HsnId, HSNNo: dbCharge.HSNNo } : null,
                client: dbCharge.PartyId != null ? { Id: dbCharge.PartyId, FirmName: dbCharge.clientName } : null,
              };
            }
          });
          setCharges(slots);
        }
      })
      .catch(err => console.error('Failed to fetch charges:', err))
      .finally(() => setLoading(false));
  }, [companyId, connectionString]);

  useEffect(() => {
    if (!companyId || !connectionString) return;
    axios.get(`${connectionString}/api/product-get-data/api/gst`, { params: { companyId } })
      .then(res => setGstOptions(Array.isArray(res.data) ? res.data : []))
      .catch(() => setGstOptions([]));
    axios.get(`${connectionString}/api/product-get-data/api/hsn`, { params: { companyId } })
      .then(res => setHsnOptions(Array.isArray(res.data) ? res.data : []))
      .catch(() => setHsnOptions([]));
  }, [companyId, connectionString]);

  // ✅ NEW: Check if submit button should be disabled based on validation rules
  const isSubmitDisabled = useMemo(() => {
    // Button is disabled if any charge violates the rules:
    // 1. If ChargeName is filled → HSN, Client are required
    // 2. If ColToBeAsk (checkbox) is selected → ChargeName, HSN, Client are required
    // 3. If TaxEnabled → ChargeName, HSN, Client, and Tax are required
    // 4. If any of HSN/Tax/Client is selected → ChargeName must be filled
    for (const c of charges) {
      const hasChargeName = c.ChargeName && c.ChargeName.trim();
      const hasHsn = !!c.hsn;
      const hasTax = !!c.tax;
      const hasClient = !!c.client;
      const isChecked = c.ColToBeAsk;
      const isTaxEnabled = c.TaxEnabled;

      // Rule 1: If ChargeName filled → HSN and Client required
      if (hasChargeName && (!hasHsn || !hasClient)) {
        return true;
      }

      // Rule 2: If checkbox selected → ChargeName, HSN, Client required
      if (isChecked && (!hasChargeName || !hasHsn || !hasClient)) {
        return true;
      }

      // Rule 3: If TaxEnabled → ChargeName, HSN, Client, Tax required
      if (isTaxEnabled && (!hasChargeName || !hasHsn || !hasClient || !hasTax)) {
        return true;
      }

      // Rule 4: If any HSN/Tax/Client selected → ChargeName required
      if ((hasHsn || hasTax || hasClient) && !hasChargeName) {
        return true;
      }

      // Rule 5: If Tax is enabled but not selected → disable
      if (isTaxEnabled && !hasTax) {
        return true;
      }

      // Rule 6: If HSN selected but not Client → disable (and vice versa)
      if ((hasHsn || hasClient) && (!hasHsn || !hasClient)) {
        return true;
      }
    }
    return false;
  }, [charges]);

  const updateCharge = (idx, patch) => {
    setCharges(prev => prev.map((c, i) => i === idx ? { ...c, ...patch } : c));
    setChargeErrors(prev => {
      const cur = prev[idx];
      if (!cur) return prev;
      const nextSlot = { ...cur };
      if (patch.client) delete nextSlot.client;
      if (patch.hsn) delete nextSlot.hsn;
      if (patch.tax) delete nextSlot.tax;
      if (Object.keys(nextSlot).length === Object.keys(cur).length) return prev;
      const next = { ...prev };
      if (Object.keys(nextSlot).length > 0) next[idx] = nextSlot; else delete next[idx];
      return next;
    });
  };

  const handleSave = async () => {
    if (!companyId) return;

    const invalidCharges = {};
    charges.forEach((c, i) => {
      if (!c.ColToBeAsk) return;
      const errs = {};
      if (!c.client) errs.client = true;
      if (!c.hsn) errs.hsn = true;
      if (c.TaxEnabled && !c.tax) errs.tax = true;
      if (Object.keys(errs).length > 0) invalidCharges[i] = errs;
    });
    if (Object.keys(invalidCharges).length > 0) {
      setChargeErrors(invalidCharges);
      return;
    }
    setChargeErrors({});

    setSaving(true);

    try {
      const chargeRows = charges.map((c, idx) => ({
        CompID: companyId,
        SrNo: idx + 1,
        TransType: 200 + idx,
        ColToBeAsk: c.ColToBeAsk,
        ChargeName: c.ChargeName || null,
        TaxEnabled: c.TaxEnabled,
        TaxId: c.tax?.TaxID ?? null,
        HsnId: c.hsn?.HSNId ?? null,
        PartyId: c.client?.Id ?? null,
        EditUserId: userId ?? null,
        EditUserDateTime: new Date().toISOString(),
      }));

      const chargeDescriptions = charges
        .filter(c => c.ChargeName && c.ChargeName.trim())
        .map((c, idx) => ({
          transType: 200 + idx,
          description: c.ChargeName,
        }));

      const payload = {
        companyId,
        fYear: getCurrentFYear(),
        additionalCharges: chargeRows,
        chargeDescriptions,
      };

      const res = await axios.post(`${connectionString}/api/userdefinecharges`, payload);
      if (res.data.success) {
        navigate(-1);
      }
    } catch (err) {
      console.error('Save error:', err);
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <Box sx={{ height: `calc(100vh - ${APPBAR_HEIGHT}px)`, bgcolor: C.surface, display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ height: `calc(100vh - ${APPBAR_HEIGHT}px)`, bgcolor: C.surface, display: 'flex', flexDirection: 'column', fontFamily: '"Inter", "Segoe UI", sans-serif', overflow: 'hidden' }}>
      <Box sx={{ bgcolor: C.blue, px: 3, display: 'flex', alignItems: 'center', gap: 2, minHeight: 54, boxShadow: '0 2px 8px rgba(24,119,242,0.25)', flexShrink: 0 }}>
        <ViewColumnIcon sx={{ fontSize: '1.1rem', color: 'rgba(255,255,255,0.85)' }} />
        <Box sx={{ mr: 'auto' }}>
          <Typography sx={{ fontSize: '0.85rem', fontWeight: 700, color: C.white, lineHeight: 1.3 }}>
            Additional Charges
          </Typography>
          <Typography sx={{ fontSize: '0.65rem', color: 'rgba(255,255,255,0.7)', lineHeight: 1.2 }}>
            Configure up to 5 extra charge fields (global)
          </Typography>
        </Box>
      </Box>

      <Box sx={{ flex: 1, minHeight: 0, p: 3, overflowY: 'auto' }}>
        <Box sx={{
          bgcolor: C.white,
          border: `1px solid ${C.grey200}`,
          borderRadius: '10px',
          boxShadow: '0 1px 4px rgba(0,0,0,0.06)',
          overflow: 'hidden',
        }}>
          <Box sx={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fill, minmax(220px, 1fr))',
            gap: 1.5,
            p: 1.5,
            alignItems: 'start',
          }}>
            {charges.map((charge, idx) => (
              <AdditionalChargeBlock
                key={idx}
                index={idx + 1}
                charge={charge}
                errors={chargeErrors[idx] || {}}
                gstOptions={gstOptions}
                hsnOptions={hsnOptions}
                connectionString={connectionString}
                companyId={companyId}
                onChange={patch => updateCharge(idx, patch)}
              />
            ))}
          </Box>
        </Box>
      </Box>

      <Box sx={{ bgcolor: C.white, borderTop: `1px solid ${C.grey200}`, p: 2, display: 'flex', gap: 1, justifyContent: 'flex-end', flexShrink: 0 }}>
        <Button
          variant="outlined" size="small"
          onClick={() => navigate(-1)}
          sx={{ fontSize: '0.75rem', textTransform: 'none', fontWeight: 600 }}
        >
          Cancel
        </Button>
        <Button
          variant="contained" size="small"
          startIcon={saving ? <CircularProgress size={12} sx={{ color: C.white }} /> : <SaveIcon sx={{ fontSize: '0.85rem' }} />}
          onClick={handleSave}
          disabled={loading || saving || isSubmitDisabled}
          sx={{
            fontSize: '0.75rem', textTransform: 'none', fontWeight: 700,
            bgcolor: C.blue, borderRadius: '7px', px: 2.5, py: 0.6,
            boxShadow: 'none',
            '&:hover': { bgcolor: C.blueDark, boxShadow: 'none' },
            opacity: (loading || saving || isSubmitDisabled) ? 0.6 : 1,
            cursor: (loading || saving || isSubmitDisabled) ? 'not-allowed' : 'pointer',
          }}
        >
          {saving ? 'Saving…' : 'Save Configuration'}
        </Button>
      </Box>
    </Box>
  );
}

function RequiredLabel({ children, required }) {
  return (
    <Typography sx={{ fontSize: '0.65rem', fontWeight: 600, color: C.grey600, mb: -0.4 }}>
      {children}{required && <span style={{ color: C.danger }}> *</span>}
    </Typography>
  );
}

function AdditionalChargeBlock({ index, charge, errors, gstOptions, hsnOptions, connectionString, companyId, onChange }) {
  const chargeLabel = charge.ChargeName || `Charge ${index}`;

  return (
    <Box sx={{
      border: `1px solid ${C.grey200}`,
      borderRadius: '8px',
      p: 1.5,
      display: 'flex', flexDirection: 'column', gap: 1,
      bgcolor: charge.ColToBeAsk ? C.blueLight : C.white,
    }}>
      <Typography sx={{ fontSize: '0.65rem', fontWeight: 600, color: C.grey600 }}>
        Additional Charge {index} *
      </Typography>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <Checkbox
          size="small"
          checked={charge.ColToBeAsk}
          onChange={e => onChange({ ColToBeAsk: e.target.checked })}
          icon={<CheckBoxBlankIcon sx={{ fontSize: '1.1rem', color: C.grey300 }} />}
          checkedIcon={<CheckBoxIcon sx={{ fontSize: '1.1rem', color: C.blue }} />}
          sx={{ p: 0.3 }}
        />
        <TextField
          size="small"
          fullWidth
          placeholder={`Additional Charge ${index}`}
          value={charge.ChargeName}
          onChange={e => onChange({ ChargeName: e.target.value })}
          sx={{
            '& .MuiInputBase-input': { fontSize: '0.8rem', py: '7px' },
            '& .MuiOutlinedInput-root': { borderRadius: '6px' },
          }}
        />
      </Box>

      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, pl: 0.5 }}>
        <Switch
          checked={charge.TaxEnabled}
          onChange={e => onChange({ TaxEnabled: e.target.checked })}
          size="small"
          sx={ios}
        />
        <Typography sx={{ fontSize: '0.75rem', color: C.grey600 }}>
          Enable tax for {chargeLabel}
        </Typography>
      </Box>

      <RequiredLabel required={charge.TaxEnabled}>Select Tax</RequiredLabel>
      <FormControl size="small" fullWidth disabled={!charge.TaxEnabled} error={!!errors.tax}>
        <Select
          displayEmpty
          value={charge.tax?.TaxID ?? ''}
          renderValue={(selected) => {
            if (!selected || selected === '') return <span style={{ color: C.grey400 }}>Select Tax</span>;
            const tax = gstOptions.find(g => g.TaxID === selected) || charge.tax;
            return tax?.Taxcode || selected;
          }}
          onChange={e => {
            const tax = gstOptions.find(g => g.TaxID === e.target.value) || null;
            onChange({ tax });
          }}
          sx={{
            fontSize: '0.78rem',
            '& .MuiSelect-select': { py: '7px' },
            borderRadius: '6px',
          }}
        >
          <MenuItem value="" disabled sx={{ fontSize: '0.78rem' }}>Select Tax</MenuItem>
          {gstOptions.map(g => (
            <MenuItem key={g.TaxID} value={g.TaxID} sx={{ fontSize: '0.78rem' }}>
              {g.Taxcode}
            </MenuItem>
          ))}
        </Select>
      </FormControl>
      {errors.tax && (
        <Typography sx={{ fontSize: '0.65rem', color: C.danger, mt: -0.5 }}>
          Tax is required for {chargeLabel}
        </Typography>
      )}

      <RequiredLabel required>HSN/SAC</RequiredLabel>
      <Autocomplete
        size="small"
        value={charge.hsn}
        onChange={(e, newVal) => onChange({ hsn: newVal })}
        options={hsnOptions}
        getOptionLabel={opt => opt?.HSNNo ? `${opt.HSNNo}${opt.HSNDescription ? ' - ' + opt.HSNDescription : ''}` : ''}
        isOptionEqualToValue={(opt, val) => opt.HSNId === val?.HSNId}
        renderInput={params => (
          <TextField
            {...params}
            placeholder="Enter HSN/SAC"
            error={!!errors.hsn}
            sx={{
              '& .MuiInputBase-input': { fontSize: '0.78rem', py: '2px !important' },
              '& .MuiOutlinedInput-root': { borderRadius: '6px' },
            }}
          />
        )}
      />
      {errors.hsn && (
        <Typography sx={{ fontSize: '0.65rem', color: C.danger, mt: -0.5 }}>
          HSN/SAC is required for {chargeLabel}
        </Typography>
      )}

      <Typography sx={{ fontSize: '0.65rem', color: C.grey400, fontStyle: 'italic' }}>
        You can change this while creating transactions
      </Typography>

      <RequiredLabel required>Select Seperate Posting client</RequiredLabel>
      <ClientAutocomplete
        connectionString={connectionString}
        companyId={companyId}
        value={charge.client}
        onChange={client => onChange({ client })}
        error={!!errors.client}
      />
      {errors.client && (
        <Typography sx={{ fontSize: '0.65rem', color: C.danger, mt: -0.5 }}>
          Select Seperate Posting client is required for {chargeLabel}
        </Typography>
      )}
    </Box>
  );
}

function ClientAutocomplete({ connectionString, companyId, value, onChange, error }) {
  const [inputValue, setInputValue] = useState('');
  const [options, setOptions] = useState([]);
  const [loading, setLoading] = useState(false);
  const debounceRef = useRef(null);

  useEffect(() => {
    if (!companyId || !connectionString) return;
    if (debounceRef.current) clearTimeout(debounceRef.current);

    debounceRef.current = setTimeout(() => {
      setLoading(true);
      axios.get(`${connectionString}/api/company-search`, {
        params: { field: 'FirmName', search: inputValue, companyId },
      })
        .then(res => {
          const data = Array.isArray(res.data.data) ? res.data.data : [];
          setOptions(data.map(row => ({ Id: row.id, FirmName: row.FirmName })));
        })
        .catch(() => setOptions([]))
        .finally(() => setLoading(false));
    }, 300);

    return () => clearTimeout(debounceRef.current);
  }, [inputValue, companyId, connectionString]);

  return (
    <Autocomplete
      size="small"
      value={value}
      onChange={(e, newVal) => {
        onChange(newVal);
        // When selecting an item, clear the search input to show the selected value
        if (newVal?.FirmName) {
          setInputValue(newVal.FirmName);
        }
      }}
      inputValue={inputValue}
      onInputChange={(e, newInput) => {
        setInputValue(newInput);
      }}
      options={options}
      loading={loading}
      filterOptions={x => x}
      getOptionLabel={opt => opt?.FirmName || ''}
      isOptionEqualToValue={(opt, val) => opt.Id === val?.Id}
      noOptionsText={loading ? 'Loading...' : 'No options'}
      renderInput={params => (
        <TextField
          {...params}
          placeholder="Select Seperate Posting client"
          error={error}
          InputProps={{
            ...params.InputProps,
            endAdornment: (
              <>
                {loading ? <CircularProgress size={12} /> : null}
                {params.InputProps.endAdornment}
              </>
            ),
          }}
          sx={{
            '& .MuiInputBase-input': { fontSize: '0.78rem', py: '2px !important' },
            '& .MuiOutlinedInput-root': { borderRadius: '6px' },
          }}
        />
      )}
    />
  );
}
